import type { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faCircleDashed as faCircleDashedDuotone } from '@fortawesome/pro-duotone-svg-icons';
import { faCircleDashed } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import type { JSX, ReactNode } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { EOnboardingTaskState } from '@feathr/blackbox';
import type { IFontAwesomeIconProps, IWellProps } from '@feathr/components';
import { Button, Icon, Well } from '@feathr/components';

import MilestoneTaskStatus from './MilestoneTaskStatus';

interface IMilestoneTaskStatus {
  icon: IconDefinition;
  iconTheme: IFontAwesomeIconProps['theme'];
  theme: IWellProps['theme'];
}

export interface IMilestoneTaskProps {
  children?: ReactNode | ReactNode[];
  className?: string;
  contentClassName?: string;
  description: ReactNode;
  disabled?: boolean;
  labelClassName?: string;
  name?: string;
  onSkip?: () => void;
  state?: EOnboardingTaskState;
  title: ReactNode;
  width?: 'narrow' | 'wide' | 'full' | 'auto';
}

const statusMap: Record<EOnboardingTaskState, IMilestoneTaskStatus> = {
  [EOnboardingTaskState.NotStarted]: {
    icon: faCircleDashed,
    iconTheme: 'neutral',
    theme: 'neutral',
  },
  [EOnboardingTaskState.InProgress]: {
    icon: faCircleDashedDuotone,
    iconTheme: 'neutral',
    theme: 'neutral',
  },
  [EOnboardingTaskState.Completed]: {
    icon: faCircleCheck,
    iconTheme: 'green',
    theme: 'green',
  },
  [EOnboardingTaskState.Skipped]: {
    icon: faCircleCheck,
    iconTheme: 'green',
    theme: 'green',
  },
};

function MilestoneTask({
  children,
  className,
  contentClassName,
  description,
  disabled,
  labelClassName,
  name,
  onSkip,
  state = EOnboardingTaskState.NotStarted,
  title,
  width = 'auto',
}: Readonly<IMilestoneTaskProps>): JSX.Element {
  const { t } = useTranslation();

  const renderStatus = (): JSX.Element | null => {
    if ([EOnboardingTaskState.NotStarted, EOnboardingTaskState.InProgress].includes(state)) {
      return null;
    }
    return <MilestoneTaskStatus status={state} />;
  };
  const renderActions = (): JSX.Element | null => {
    if (!onSkip) {
      return null;
    }
    return (
      <div>
        <Button disabled={disabled} name={'milestone-task-skip'} onClick={onSkip} type={'tertiary'}>
          {t('Skip')}
        </Button>
      </div>
    );
  };

  return (
    <Well
      className={className}
      contentClassName={contentClassName}
      description={description}
      // TODO: Add disabled prop to Well component (no issue)
      label={title}
      labelClassName={labelClassName}
      layout={'horizontal'}
      name={name}
      prefix={<Icon icon={statusMap[state].icon} theme={statusMap[state].iconTheme} />}
      theme={statusMap[state].theme}
      width={width}
    >
      {renderStatus()}
      {renderActions()}
      {children}
    </Well>
  );
}

export default MilestoneTask;
